import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import {
  DataTable,
  TableColumn,
} from "@/modules/shared/ui/components/custom/CustomTable"
import { useEmailTemplates } from "../../hooks/useEmailTemplates"
import type { EmailTemplateDto } from "../../dtos/email-templates-dto"

function humanizeType(type: string): string {
  return type.replace(/([A-Z])/g, " $1").trim()
}

type EmailTemplateRow = EmailTemplateDto & { id: string }

export default function EmailTemplatesListPage() {
  const navigate = useNavigate()
  const organizationId = useSelector(selectCurrentOrganizationId)
  const { data: templates = [], isLoading } = useEmailTemplates(
    organizationId ?? undefined,
  )

  // Backend flags system-only templates (OtpCode, ResetPassword, SubscriptionActivated,
  // PaymentFailed) — keep that list server-side so it can't drift between layers.
  const rows: EmailTemplateRow[] = templates
    .filter((t) => !t.isSystemOnly)
    .map((t) => ({ ...t, id: t.type }))

  const columns: TableColumn<EmailTemplateRow>[] = [
    {
      key: "type",
      label: "Template",
      render: (t) => (
        <span className="font-medium text-foreground">
          {humanizeType(t.type)}
        </span>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      render: (t) => {
        const parts = t.subject.split(/({{[^}]+}})/)
        return (
          <span className="text-muted-foreground">
            {parts.map((part, i) =>
              part.startsWith("{{") && part.endsWith("}}") ? (
                <span
                  key={i}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary mx-0.5"
                >
                  {t.subjectSampleData?.[part] ?? t.sampleData?.[part] ?? part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </span>
        )
      },
    },
    {
      key: "customBody",
      label: "Status",
      render: (t) =>
        t.customBody ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
            Customised
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
            Default
          </span>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          Email Templates
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Customise the emails your organisation sends to candidates and
          employees.
        </p>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <DataTable<EmailTemplateRow>
            columns={columns}
            data={rows}
            emptyMessage="No email templates found."
            actions={(t) => [
              {
                label: "Edit",
                onClick: () => navigate(`/settings/email-templates/${t.type}`),
              },
            ]}
          />
        )}
      </div>
    </div>
  )
}
